import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { addDays, format, isWithinInterval, parseISO, startOfWeek } from 'date-fns';
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap } from 'rxjs';

export interface MauiOfficeClosedHoliday {
  date: string;
  label: string;
  fullName: string;
}

interface MauiSession {
  id: number;
  startDate?: string;
  endDate?: string;
}

type MauiSessionsResponse = MauiSession[] | { value?: MauiSession[] };

interface MauiLookup {
  naturalKey?: string;
  shortDescription?: string;
  description?: string;
  webDescription?: string;
  additionalInfo?: Record<string, string>;
}

interface MauiSessionDate {
  dateLookup?: MauiLookup;
  dateCategoryLookups?: MauiLookup[];
  beginDate?: string;
  endDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MauiHolidaysService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'https://api.maui.uiowa.edu/maui/api/pub/registrar';
  private sessions$?: Observable<MauiSession[]>;
  private readonly sessionHolidayCache = new Map<number, Observable<MauiSessionDate[]>>();
  private readonly weekHolidayCache = new Map<string, Observable<Record<string, MauiOfficeClosedHoliday>>>();

  getOfficeClosedHolidaysForWeek(weekStart: Date): Observable<Record<string, MauiOfficeClosedHoliday>> {
    const start = startOfWeek(weekStart);
    const weekKey = format(start, 'yyyy-MM-dd');
    const cached = this.weekHolidayCache.get(weekKey);
    if (cached) return cached;

    const weekdays = Array.from({ length: 5 }, (_, i) => format(addDays(start, i + 1), 'yyyy-MM-dd'));
    const request = this.getSessions().pipe(
      switchMap(sessions => {
        const sessionIds = this.sessionIdsForDates(sessions, weekdays);
        if (!sessionIds.length) return of([]);
        return forkJoin(sessionIds.map(id => this.getSessionHolidays(id))).pipe(
          map(results => results.flat())
        );
      }),
      map(sessionDates => this.toOfficeClosedHolidays(sessionDates, weekdays)),
      catchError(() => of({})),
      shareReplay(1)
    );

    this.weekHolidayCache.set(weekKey, request);
    return request;
  }

  private getSessions(): Observable<MauiSession[]> {
    if (this.sessions$) return this.sessions$;

    this.sessions$ = this.http.get<MauiSessionsResponse>(`${this.baseUrl}/sessions`).pipe(
      map(response => Array.isArray(response) ? response : response.value ?? []),
      catchError(() => of([])),
      shareReplay(1)
    );

    return this.sessions$;
  }

  private sessionIdsForDates(sessions: MauiSession[], dates: string[]): number[] {
    const sessionIds = new Set<number>();
    const weekdays = dates.map(date => parseISO(date));

    for (const session of sessions) {
      if (!session.id || !session.startDate || !session.endDate) continue;

      const start = parseISO(session.startDate);
      const end = parseISO(session.endDate);
      if (weekdays.some(day => isWithinInterval(day, { start, end }))) {
        sessionIds.add(session.id);
      }
    }

    return [...sessionIds];
  }

  private getSessionHolidays(sessionId: number): Observable<MauiSessionDate[]> {
    const cached = this.sessionHolidayCache.get(sessionId);
    if (cached) return cached;

    const request = this.http.get<MauiSessionDate[]>(`${this.baseUrl}/session-dates/holidays/${sessionId}`).pipe(
      catchError(() => of([])),
      shareReplay(1)
    );

    this.sessionHolidayCache.set(sessionId, request);
    return request;
  }

  private toOfficeClosedHolidays(sessionDates: MauiSessionDate[], weekdays: string[]): Record<string, MauiOfficeClosedHoliday> {
    const holidays: Record<string, MauiOfficeClosedHoliday> = {};
    const weekdayDates = weekdays.map(day => ({ key: day, date: parseISO(day) }));

    for (const sessionDate of sessionDates) {
      if (!this.isOfficeClosedHoliday(sessionDate) || !sessionDate.beginDate || !sessionDate.endDate) continue;

      const begin = parseISO(sessionDate.beginDate);
      const end = parseISO(sessionDate.endDate);
      const label = this.cleanLabel(sessionDate.dateLookup?.shortDescription || sessionDate.dateLookup?.description || 'Holiday');
      const fullName = this.cleanFullName(sessionDate.dateLookup?.description || sessionDate.dateLookup?.shortDescription || label);

      for (const day of weekdayDates) {
        if (holidays[day.key]) continue;
        if (isWithinInterval(day.date, { start: begin, end })) {
          holidays[day.key] = { date: day.key, label, fullName };
        }
      }
    }

    return holidays;
  }

  private isOfficeClosedHoliday(sessionDate: MauiSessionDate): boolean {
    const isHoliday = sessionDate.dateLookup?.additionalInfo?.['holiday'] === 'true';
    const officesClosed = sessionDate.dateCategoryLookups?.some(category =>
      category.naturalKey === 'UNIVERSITY_OFFICES_CLOSED'
      || category.description?.toLowerCase() === 'university offices closed'
    );
    return !!isHoliday && !!officesClosed;
  }

  private cleanLabel(label: string): string {
    return label
      .replace(/^Holiday\s*[-(]\s*/i, '')
      .replace(/\)$/g, '')
      .trim();
  }

  private cleanFullName(name: string): string {
    return name
      .replace(/^University Holiday\s*[-(]\s*/i, '')
      .replace(/\)$/g, '')
      .trim();
  }
}
